/**
 * __FLEET_TRACKING__ (2026-10-03) the Google map behind the Fleet Tracking view.
 *
 * Imperative on purpose: truck markers are ONE OverlayView of plain DOM nodes (heading arrow +
 * name tag) that are moved, not re-created, every playback tick; polylines / small markers are
 * reused by index (setOptions) so a scrubbing time bar never flickers.
 *
 * Props are plain data; the parent (FleetTrackingView) owns all state.
 */
import React, { useEffect, useRef } from 'react';

const DARK_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#4b5563' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** Truck markers as DOM nodes in one overlay. */
const makeFleetOverlay = (gm, handlersRef) => {
  class FleetOverlay extends gm.OverlayView {
    constructor() {
      super();
      this.items = [];
      this.els = new Map();
      this.layer = null;
    }

    onAdd() {
      this.layer = document.createElement('div');
      this.layer.style.position = 'absolute';
      this.getPanes().overlayMouseTarget.appendChild(this.layer);
    }

    onRemove() {
      if (this.layer && this.layer.parentNode) this.layer.parentNode.removeChild(this.layer);
      this.layer = null;
      this.els.clear();
    }

    setItems(items) {
      this.items = items || [];
      this.draw();
    }

    makeEl(id) {
      const el = document.createElement('div');
      el.style.cssText = 'position:absolute;left:0;top:0;cursor:pointer;will-change:transform;';
      el.addEventListener('mouseenter', (e) => handlersRef.current.hover && handlersRef.current.hover(id, e));
      el.addEventListener('mousemove', (e) => handlersRef.current.hover && handlersRef.current.hover(id, e));
      el.addEventListener('mouseleave', () => handlersRef.current.out && handlersRef.current.out(id));
      el.addEventListener('click', (e) => { e.stopPropagation(); if (handlersRef.current.click) handlersRef.current.click(id); });
      if (gm.OverlayView.preventMapHitsAndGesturesFrom) gm.OverlayView.preventMapHitsAndGesturesFrom(el);
      return el;
    }

    draw() {
      const proj = this.getProjection();
      if (!proj || !this.layer) return;
      const seen = new Set();
      this.items.forEach((it) => {
        seen.add(it.id);
        let el = this.els.get(it.id);
        if (!el) { el = this.makeEl(it.id); this.layer.appendChild(el); this.els.set(it.id, el); }
        const pt = proj.fromLatLngToDivPixel(new gm.LatLng(it.lat, it.lng));
        el.style.transform = `translate(${Math.round(pt.x)}px, ${Math.round(pt.y)}px)`;
        el.style.zIndex = it.selected ? 1000 : (it.faded ? 1 : 10);
        const key = [it.arrow, Math.round(it.hdg || 0), it.color, it.label, it.sub, it.selected, it.faded, it.alert].join('|');
        if (el.dataset.k !== key) {
          el.dataset.k = key;
          const size = it.selected ? 30 : 24;
          const ring = it.selected ? '#f0f6fc' : (it.alert ? '#f85149' : '#0d1117');
          const icon = it.arrow
            ? `<svg width="${size}" height="${size}" viewBox="-12 -12 24 24" style="transform:rotate(${Math.round(it.hdg || 0)}deg);display:block">
                 <path d="M0,-10 L7,8 L0,4 L-7,8 Z" fill="${it.color}" stroke="${ring}" stroke-width="2" stroke-linejoin="round"/></svg>`
            : `<svg width="${size}" height="${size}" viewBox="-12 -12 24 24" style="display:block">
                 <circle r="7.5" fill="${it.color}" stroke="${ring}" stroke-width="2.5"/>
                 ${it.faded ? '' : '<circle r="2.5" fill="#0d1117"/>'}</svg>`;
          el.style.opacity = it.faded ? '0.55' : '1';
          el.innerHTML = `<div style="position:absolute;left:${-size / 2}px;top:${-size / 2}px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.7))">${icon}</div>`
            + `<div style="position:absolute;left:0;top:${size / 2 + 2}px;transform:translateX(-50%);white-space:nowrap;`
            + `background:rgba(13,17,23,.88);border:1px solid ${it.selected ? '#f0f6fc' : (it.alert ? '#f85149' : '#30363d')};color:#f0f6fc;`
            + `font:600 11px/1.3 -apple-system,Segoe UI,Roboto,sans-serif;padding:1px 6px;border-radius:9px;pointer-events:none">`
            + `${esc(it.label)}${it.sub ? `<span style="color:${it.alert ? '#f85149' : '#8b949e'};font-weight:500"> · ${esc(it.sub)}</span>` : ''}</div>`;
        }
      });
      Array.from(this.els.keys()).forEach((id) => {
        if (!seen.has(id)) { const el = this.els.get(id); if (el.parentNode) el.parentNode.removeChild(el); this.els.delete(id); }
      });
    }
  }
  return new FleetOverlay();
};

/** Reuse a pool of google objects by index: create / setOptions / hide the rest. */
const syncPool = (pool, specs, create, map) => {
  specs.forEach((spec, i) => {
    if (!pool[i]) pool[i] = create(spec);
    else pool[i].setOptions(spec);
    if (pool[i].getMap() !== map) pool[i].setMap(map);
  });
  for (let i = specs.length; i < pool.length; i++) if (pool[i].getMap()) pool[i].setMap(null);
};

function FleetTrackingMap({
  ready, markers, polylines, dots, rects, measurePath,
  onMarkerHover, onMarkerOut, onMarkerClick, onMapClick, onDotClick,
  fitToken, fitPoints, panTarget, cursorStyle,
}) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const handlersRef = useRef({});
  const pools = useRef({ lines: [], dots: [], rects: [], measure: [] });

  handlersRef.current = { hover: onMarkerHover, out: onMarkerOut, click: onMarkerClick, map: onMapClick, dot: onDotClick };

  // Create the map once Google Maps is loaded.
  useEffect(() => {
    if (!ready || mapRef.current || !divRef.current || !window.google || !window.google.maps) return;
    const gm = window.google.maps;
    const map = new gm.Map(divRef.current, {
      center: { lat: 43.9, lng: -80.4 },
      zoom: 8,
      styles: DARK_STYLE,
      mapTypeControl: true,
      mapTypeControlOptions: { position: gm.ControlPosition.TOP_RIGHT },
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
      gestureHandling: 'greedy',
    });
    map.addListener('click', (e) => {
      if (handlersRef.current.map && e.latLng) handlersRef.current.map({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });
    const ov = makeFleetOverlay(gm, handlersRef);
    ov.setMap(map);
    mapRef.current = map;
    overlayRef.current = ov;
  }, [ready]);

  // Unmount: detach everything.
  useEffect(() => () => {
    const p = pools.current;
    [p.lines, p.dots, p.rects, p.measure].forEach((arr) => arr.forEach((o) => o.setMap(null)));
    if (overlayRef.current) overlayRef.current.setMap(null);
    mapRef.current = null;
  }, []);

  useEffect(() => {
    if (overlayRef.current) overlayRef.current.setItems(markers || []);
  }, [markers, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const gm = window.google.maps;
    const specs = (polylines || []).map((l) => ({
      path: l.path,
      strokeColor: l.color,
      strokeOpacity: l.dashed ? 0 : (l.opacity != null ? l.opacity : 0.9),
      strokeWeight: l.weight || 3,
      zIndex: l.z || 1,
      clickable: false,
      icons: l.dashed ? [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, strokeColor: l.color, scale: 2 }, offset: '0', repeat: '10px' }] : null,
    }));
    syncPool(pools.current.lines, specs, (s) => new gm.Polyline(s), map);
  }, [polylines, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const gm = window.google.maps;
    const specs = (dots || []).map((d) => ({
      position: { lat: d.lat, lng: d.lng },
      title: d.title || '',
      zIndex: d.z || 50,
      label: d.label ? { text: String(d.label), color: '#0d1117', fontSize: '10px', fontWeight: '700' } : null,
      icon: {
        path: d.shape === 'square' ? 'M -1,-1 1,-1 1,1 -1,1 z' : (d.shape === 'diamond' ? 'M 0,-1.3 1.3,0 0,1.3 -1.3,0 z' : gm.SymbolPath.CIRCLE),
        scale: d.size || 6,
        fillColor: d.color,
        fillOpacity: 1,
        strokeColor: d.stroke || '#0d1117',
        strokeWeight: 1.5,
      },
    }));
    const pool = pools.current.dots;
    syncPool(pool, specs, (s) => {
      const mk = new gm.Marker(s);
      mk.addListener('click', () => { const idx = pool.indexOf(mk); if (handlersRef.current.dot && idx >= 0) handlersRef.current.dot(idx); });
      return mk;
    }, map);
  }, [dots, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const gm = window.google.maps;
    const specs = (rects || []).map((r) => ({
      bounds: { south: r.lat, west: r.lng, north: r.lat + r.size, east: r.lng + r.size },
      strokeWeight: 0,
      fillColor: r.color,
      fillOpacity: r.opacity,
      clickable: false,
      zIndex: 0,
    }));
    syncPool(pools.current.rects, specs, (s) => new gm.Rectangle(s), map);
  }, [rects, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const gm = window.google.maps;
    const pts = measurePath || [];
    const specs = [];
    if (pts.length >= 2) specs.push({ path: pts, strokeColor: '#f0f6fc', strokeOpacity: 1, strokeWeight: 2, zIndex: 900, clickable: false, icons: null });
    syncPool(pools.current.measure, specs, (s) => new gm.Polyline(s), map);
  }, [measurePath, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fitToken) return;
    const gm = window.google.maps;
    const pts = (fitPoints || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (!pts.length) return;
    if (pts.length === 1) { map.setCenter(pts[0]); map.setZoom(13); return; }
    const b = new gm.LatLngBounds();
    pts.forEach((p) => b.extend(p));
    map.fitBounds(b, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitToken, ready]);

  useEffect(() => {
    if (mapRef.current) mapRef.current.setOptions({ draggableCursor: cursorStyle || null });
  }, [cursorStyle, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !panTarget) return;
    map.panTo(panTarget);
  }, [panTarget, ready]);

  return <div ref={divRef} style={{ position: 'absolute', inset: 0 }} />;
}

export default FleetTrackingMap;
